import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { api } from '@/lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BarChart3, TrendingUp } from 'lucide-react';

interface OperadoresGraficoMensalProps {
  selectedCompany?: string;
}

// Cores para as barras dos operadores
const CORES_OPERADORES = [
  '#3B82F6', // blue-500
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#06B6D4', // cyan-500
  '#84CC16', // lime-500
  '#F97316', // orange-500
  '#EC4899', // pink-500
  '#6366F1', // indigo-500
];

export const OperadoresGraficoMensal: React.FC<OperadoresGraficoMensalProps> = ({
  selectedCompany
}) => {
  // Buscar dados do gráfico
  const { data: graficoData, isLoading, error } = useQuery({
    queryKey: ['operadores-grafico-mensal', selectedCompany],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCompany) {
        params.append('companyId', selectedCompany);
      }
      
      const response = await api.get(`/reports/operadores-grafico-mensal?${params.toString()}`);
      return response.data.data;
    },
    refetchInterval: 30000, // Atualizar a cada 30 segundos
    refetchIntervalInBackground: true, // Continuar atualizando mesmo com aba inativa
  });

  const dadosGrafico = graficoData?.dadosGrafico || [];

  // Extrair nomes dos operadores para criar as barras
  const operadores = React.useMemo(() => {
    if (dadosGrafico.length === 0) return [];
    
    const operadoresSet = new Set<string>();
    dadosGrafico.forEach((dia: any) => {
      Object.keys(dia).forEach(key => {
        if (key !== 'dia' && key !== 'diaFormatado') {
          operadoresSet.add(key);
        }
      });
    });
    
    return Array.from(operadoresSet);
  }, [dadosGrafico]);

  // Calcular totais por operador
  const totaisPorOperador = React.useMemo(() => {
    const totais: { [key: string]: number } = {};
    
    operadores.forEach(operador => {
      totais[operador] = dadosGrafico.reduce((total: number, dia: any) => {
        return total + (dia[operador] || 0);
      }, 0);
    });
    
    return totais;
  }, [dadosGrafico, operadores]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Atividade dos Operadores - Mês Atual</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Atividade dos Operadores - Mês Atual</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-500">Erro ao carregar dados do gráfico</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (dadosGrafico.length === 0 || operadores.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Atividade dos Operadores - Mês Atual</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma atividade registrada este mês</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Atividade dos Operadores - Mês Atual</span>
          </div>
          <div className="flex items-center space-x-1 text-sm text-green-600">
            <TrendingUp className="h-4 w-4" />
            <span>Contatos por dia</span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Resumo dos totais */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
            Total de Contatos no Mês:
          </h4>
          <div className="flex flex-wrap gap-3">
            {operadores.map((operador, index) => (
              <div 
                key={operador}
                className="flex items-center space-x-2 text-sm"
              >
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: CORES_OPERADORES[index % CORES_OPERADORES.length] }}
                />
                <span className="font-medium">{operador}:</span>
                <span className="text-blue-600 font-semibold">
                  {totaisPorOperador[operador]}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Gráfico */}
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={dadosGrafico}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="diaFormatado"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(label) => `Dia: ${label}`}
                formatter={(value, name) => [value, name]}
              />
              <Legend />
              {operadores.map((operador, index) => (
                <Bar 
                  key={operador}
                  dataKey={operador}
                  fill={CORES_OPERADORES[index % CORES_OPERADORES.length]}
                  name={operador}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
